import {
  ChangeDetectionStrategy, Component, ElementRef, EventEmitter, HostListener,
  Input, OnChanges, Output, SimpleChanges, computed, signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { FormDataApprover } from '../../models/licext.model';

/**
 * Approver-selection wizard, rendered inline within an agent message bubble when
 * the License Extension approval pre-check flags at least one item as needing
 * approval.
 *
 * Mirrors the wizard mock: a multi-select region filter, a search box (name /
 * email / region), a paginated checkbox list, select-all / deselect-all, and a
 * "Send for Approval (N selected)" action that is disabled until ≥1 approver is
 * picked. The component is a pure renderer — it owns only local selection / filter
 * state and emits the chosen approvers; the host renderer composes the seed
 * message and sends it through the chat pipeline. Theme-aware (uses --wg-* vars).
 */
@Component({
  selector: 'app-licext-approver-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './licext-approver-picker.component.html',
  styleUrls: ['./licext-approver-picker.component.scss'],
})
export class LicextApproverPickerComponent implements OnChanges {
  /** The full cross-region approver list emitted by the backend. */
  @Input() approvers: FormDataApprover[] = [];
  /** True when this message is no longer the latest / already submitted → read-only. */
  @Input() locked = false;
  /** Label for the submit button. Chat flow keeps the default; the dashboard
   *  add-approver drawer overrides it (e.g. "Add selected approver(s)"). */
  @Input() submitLabel = 'Send for Approval';
  /** Emails to pre-select when the picker (re)opens — used to restore a prior
   *  approver selection when a form is reloaded. Empty = nothing pre-selected. */
  @Input() preselectedEmails: string[] = [];

  /** Emits the chosen approvers when the user clicks Send for Approval. */
  @Output() send = new EventEmitter<FormDataApprover[]>();

  /** How many approvers to show per page. */
  readonly PAGE_SIZE = 8;

  private readonly _approvers = signal<FormDataApprover[]>([]);
  /** Lower-cased emails of the selected approvers. */
  readonly selectedEmails = signal<Set<string>>(new Set());
  /** Selected region filters; empty = all regions. */
  readonly selectedRegions = signal<Set<string>>(new Set());
  readonly query = signal('');
  readonly page = signal(0);
  readonly regionPanelOpen = signal(false);

  constructor(private readonly host: ElementRef<HTMLElement>) {}

  private _key = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['approvers'] && !changes['preselectedEmails']) return;
    // Only reset local state when the approver payload (or the pre-selection)
    // actually changes, so unrelated change-detection never clobbers an
    // in-progress selection.
    const list = (this.approvers ?? []).filter(a => (a?.email ?? '').trim());
    const preselected = (this.preselectedEmails ?? []).map(e => (e ?? '').toLowerCase()).filter(Boolean);
    const key = JSON.stringify([list, preselected]);
    if (key === this._key) return;
    this._key = key;
    this._approvers.set(list);
    this.selectedEmails.set(new Set(preselected));
    this.selectedRegions.set(new Set());
    this.query.set('');
    this.page.set(0);
  }

  /** All regions an approver belongs to (`regions`, else the legacy single `region`). */
  private regionsOf(a: FormDataApprover): string[] {
    const list = a.regions?.length ? a.regions : [a.region ?? ''];
    return list.map(r => (r ?? '').trim()).filter(Boolean);
  }

  /** Distinct regions present in the approver list, sorted, for the filter. */
  readonly regions = computed(() => {
    const set = new Set<string>();
    for (const a of this._approvers()) {
      for (const r of this.regionsOf(a)) set.add(r);
    }
    return Array.from(set).sort((x, y) => x.localeCompare(y));
  });

  /** Approvers matching the active region filter and search query, with the
   *  currently-selected approvers floated to the top (stable within each group)
   *  so your picks stay visible on the first page. */
  readonly filtered = computed(() => {
    const regions = this.selectedRegions();
    const q = this.query().trim().toLowerCase();
    const sel = this.selectedEmails();
    const matches = this._approvers().filter(a => {
      if (regions.size && !this.regionsOf(a).some(r => regions.has(r))) return false;
      if (!q) return true;
      return (
        (a.name ?? '').toLowerCase().includes(q) ||
        (a.email ?? '').toLowerCase().includes(q) ||
        (a.region ?? '').toLowerCase().includes(q)
      );
    });
    const isSel = (a: FormDataApprover) => sel.has((a.email ?? '').toLowerCase());
    return [...matches.filter(isSel), ...matches.filter(a => !isSel(a))];
  });

  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.filtered().length / this.PAGE_SIZE)));

  /** The filtered approvers for the current page (page is clamped to range). */
  readonly pageItems = computed(() => {
    const start = Math.min(this.page(), this.totalPages() - 1) * this.PAGE_SIZE;
    return this.filtered().slice(start, start + this.PAGE_SIZE);
  });

  readonly selectedCount = computed(() => this.selectedEmails().size);

  /** True when every approver in the current filter is selected. */
  readonly allFilteredSelected = computed(() => {
    const f = this.filtered();
    if (!f.length) return false;
    const sel = this.selectedEmails();
    return f.every(a => sel.has((a.email ?? '').toLowerCase()));
  });

  isSelected(a: FormDataApprover): boolean {
    return this.selectedEmails().has((a.email ?? '').toLowerCase());
  }

  toggle(a: FormDataApprover): void {
    if (this.locked) return;
    const email = (a.email ?? '').toLowerCase();
    if (!email) return;
    this.selectedEmails.update(prev => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  }

  /** Select-all / deselect-all — acts on the CURRENTLY FILTERED set. */
  toggleSelectAll(): void {
    if (this.locked) return;
    const filtered = this.filtered();
    const allOn = this.allFilteredSelected();
    this.selectedEmails.update(prev => {
      const next = new Set(prev);
      for (const a of filtered) {
        const email = (a.email ?? '').toLowerCase();
        if (!email) continue;
        if (allOn) next.delete(email);
        else next.add(email);
      }
      return next;
    });
  }

  // ── Region filter dropdown ─────────────────────────────────────────────────

  toggleRegionPanel(): void {
    if (this.locked) return;
    this.regionPanelOpen.update(v => !v);
  }

  toggleRegion(region: string): void {
    if (this.locked) return;
    this.selectedRegions.update(prev => {
      const next = new Set(prev);
      if (next.has(region)) next.delete(region);
      else next.add(region);
      return next;
    });
    this.page.set(0);
  }

  isRegionSelected(region: string): boolean {
    return this.selectedRegions().has(region);
  }

  clearRegions(): void {
    if (this.locked) return;
    this.selectedRegions.set(new Set());
    this.page.set(0);
  }

  regionLabel(): string {
    const n = this.selectedRegions().size;
    return n === 0 ? 'All regions' : `${n} region${n === 1 ? '' : 's'}`;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.regionPanelOpen()) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest('.licext-ap__region')) return;
    this.regionPanelOpen.set(false);
  }

  // ── Search + pagination ─────────────────────────────────────────────────────

  onSearch(value: string): void {
    this.query.set(value);
    this.page.set(0);
  }

  prevPage(): void {
    if (this.page() > 0) this.page.update(p => p - 1);
  }

  nextPage(): void {
    if (this.page() < this.totalPages() - 1) this.page.update(p => p + 1);
  }

  // ── Submit ───────────────────────────────────────────────────────────────────

  submitSelection(): void {
    if (this.locked || this.selectedCount() === 0) return;
    const sel = this.selectedEmails();
    const chosen = this._approvers().filter(a => sel.has((a.email ?? '').toLowerCase()));
    this.send.emit(chosen);
  }

  trackByEmail(_i: number, a: FormDataApprover): string {
    return a.email ?? '';
  }
}
